import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { LoaderIcon } from 'lucide-react'
import { Slot as SlotPrimitive } from 'radix-ui'
import { cn } from '~/lib/utils'

const buttonVariants = cva(
  `
    focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]
    aria-invalid:border-destructive aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap
    transition-all outline-none
    disabled:pointer-events-none disabled:opacity-50
    [&_svg]:pointer-events-none [&_svg]:shrink-0
    [&_svg:not([class*=\'size-\'])]:size-4
  `,
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        sky: 'text-primary-foreground bg-[#3873ff] hover:bg-[#3873ff]/80 focus-visible:ring-[#3873ff]',
        destructive:
          `
            bg-destructive text-white shadow-xs
            hover:bg-destructive/90
            focus-visible:ring-destructive/20
            dark:bg-destructive/60 dark:focus-visible:ring-destructive/40
          `,
        outline:
          `
            bg-background border shadow-xs
            hover:bg-accent hover:text-accent-foreground
            dark:border-input dark:bg-input/30 dark:hover:bg-input/50
          `,
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        xl: 'h-16 rounded-xl px-8 text-lg',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  isLoading?: boolean
  asChild?: boolean
}

function Button({ className, variant, size, isLoading = false, asChild = false, children, ...props }: ButtonProps) {
  const Component = asChild ? SlotPrimitive.Slot : 'button'

  return (
    <Component
      className={cn(buttonVariants({ variant, size, className }), isLoading && 'disabled:opacity-100')}
      {...props}
    >
      {isLoading
        ? (
            <div className="zoom-in animate-in">
              <LoaderIcon className="animate-spin" />
            </div>
          )
        : (
            children
          )}
    </Component>
  )
}

export { Button, buttonVariants }
